import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useData } from "../DataContext";
import { LazyImage } from "../components/LazyImage";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildCollectionPageLD, buildBreadcrumbLD } from "../lib/jsonld";

const ProductCard = ({ p }) => (
  <div className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:border-sky-300 transition-all duration-300">
    <div className="aspect-square overflow-hidden bg-slate-100 flex items-center justify-center p-4">
      <LazyImage src={p.image} alt={p.name} className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="p-5">
      <h3 className="font-black text-[#0a2a5e] text-lg mb-1 group-hover:text-sky-600 transition-colors">{p.name}</h3>
      <p className="text-sm text-slate-600 leading-snug mb-4 min-h-[40px]">{p.desc}</p>
      {p.colours && (
        <div className="flex items-center gap-1.5 mb-4" data-testid={`colour-swatches-${p.slug}`}>
          {p.colours.map((c) => (
            <span
              key={c.name}
              title={c.name}
              className="h-4 w-4 rounded-full border border-slate-300 shadow-sm"
              style={{ backgroundColor: c.hex }}
            />
          ))}
          <span className="text-xs text-slate-500 ml-1">{p.colours.length} colours</span>
        </div>
      )}
      <div className="flex gap-2">
        <Button asChild className="flex-1 bg-[#0a2a5e] hover:bg-[#061a3d] text-white rounded-full">
          <Link to={`/products/${p.slug}`}>View Details</Link>
        </Button>
        <Button asChild variant="outline" className="border-[#0a2a5e] text-[#0a2a5e] hover:bg-[#0a2a5e] hover:text-white rounded-full">
          <Link to="/contact"><ArrowRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </div>
    </div>
  </div>
);

export default function Products() {
  const { PRODUCT_CATEGORIES } = useData();
  const location = useLocation();
  const [active, setActive] = useState(PRODUCT_CATEGORIES[0].id);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  useEffect(() => { setTypeFilter("All"); }, [active]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && PRODUCT_CATEGORIES.find((c) => c.id === hash)) {
      setActive(hash);
      setTimeout(() => window.scrollTo({ top: 600, behavior: "smooth" }), 100);
    }
  }, [location.hash]);

  const cat = PRODUCT_CATEGORIES.find((c) => c.id === active);
  const filtered = cat.products.filter((p) =>
    (p.name.toLowerCase().includes(query.toLowerCase()) || p.desc.toLowerCase().includes(query.toLowerCase())) &&
    (typeFilter === "All" || p.ptype === typeFilter)
  );

  const hasSeries = filtered.some((p) => p.series);
  const seriesGroups = hasSeries
    ? (cat.seriesOrder || [...new Set(filtered.map((p) => p.series))])
        .map((s) => [s, filtered.filter((p) => p.series === s)])
        .filter(([, items]) => items.length > 0)
    : null;

  usePageMeta(
    `${cat.name} — Products`,
    cat.intro || `Explore the BAISHALII ${cat.name} range — quality-certified products built to last.`
  );

  const collectionItems = (cat.products || []).map((p) => ({ name: p.name, path: `/products/${p.slug}` }));
  const collectionLd = buildCollectionPageLD({
    name: `${cat.name} — Products`,
    path: "/products",
    description: cat.intro || `Explore the BAISHALII ${cat.name} range.`,
    items: collectionItems,
  });
  const breadcrumbLd = buildBreadcrumbLD([
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
  ]);

  return (
    <div className="pt-[88px] md:pt-[112px]">
      <JsonLd blocks={[collectionLd, breadcrumbLd]} id="products-collection" />
      {/* hero */}
      <section className="relative py-24 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e] to-sky-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:"radial-gradient(circle at 30% 30%, white 1px, transparent 1px)", backgroundSize:"24px 24px"}}/>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="text-sky-300 font-semibold text-sm tracking-[0.2em] mb-3">OUR PRODUCTS</div>
          <h1 className="text-5xl md:text-6xl font-black mb-4">Complete piping ecosystem</h1>
          <p className="text-slate-200 text-lg max-w-2xl mx-auto">From plumbing &amp; drainage to industrial and fire safety — explore the full BAISHALII range.</p>
          <div className="max-w-xl mx-auto mt-8 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products..." className="pl-12 h-12 rounded-full bg-white text-slate-900 border-0 shadow-lg" />
          </div>
        </div>
      </section>

      {/* layout */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* sidebar */}
          <aside className="lg:sticky lg:top-32 h-fit bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <h3 className="font-bold text-[#0a2a5e] mb-4 text-sm tracking-wider">CATEGORIES</h3>
            <div className="space-y-1">
              {PRODUCT_CATEGORIES.map((c) => (
                <button key={c.id} onClick={() => setActive(c.id)} className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all ${active === c.id ? "bg-gradient-to-r from-sky-500 to-[#0a2a5e] text-white font-semibold shadow-md" : "text-slate-700 hover:bg-sky-50 hover:text-sky-700"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </aside>

          {/* products */}
          <div>
            <div className="flex justify-between items-end mb-6 flex-wrap gap-2">
              <h2 className="text-3xl md:text-4xl font-black text-[#0a2a5e]">{cat.name}</h2>
              <span className="text-sm text-slate-500">{filtered.length} products</span>
            </div>
            {cat.typeFilters && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="type-filter-chips">
                {cat.typeFilters.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTypeFilter(t)}
                    data-testid={`type-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${typeFilter === t ? "bg-[#0a2a5e] text-white border-[#0a2a5e] shadow-md" : "bg-white text-slate-600 border-slate-300 hover:border-sky-400 hover:text-sky-700"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <p className="text-slate-500">No products match your search.</p>
              </div>
            ) : seriesGroups ? (
              <div className="space-y-12">
                {seriesGroups.map(([series, items]) => (
                  <div key={series} data-testid={`series-section-${series.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-4 mb-5">
                      <h3 className="text-2xl font-black text-[#0a2a5e]">{series} <span className="text-sky-600">Series</span></h3>
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs text-slate-500 font-semibold">{items.length} products</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {items.map((p) => <ProductCard key={p.slug} p={p} />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((p) => <ProductCard key={p.slug} p={p} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
